import React from 'react';

import { Tooltip } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { InProgress } from '../../components/icons';
import {
    makeNiceName,
    compareVersions,
    getSessionVar,
    getValFromPath,
    isApplianceUpgradeableToVersion,
    isValidIP,
} from '../../lib/helpers';
import { RivermeadowTag } from '../../components';
import { CONFIG } from '../../constants';
import { ApplianceStates, CLOUD_TYPE } from '../../constants';

export const APPLIANCE_READINESS_STATE_INFO = {
    appliance_success_title: 'Appliance heartbeat successful',
    appliance_fail_title: 'No Appliance heartbeat',
    source_worker_success_title: 'Source Worker Appliance heartbeat successful',
    source_worker_fail_title: 'No Source Worker Appliance heartbeat',
};

export const getCloudLogo = (cloudType) => {
    switch (cloudType) {
        case 'aws':
            return 'aws_new.png';
        case 'azure':
            return 'azure.png';
        case 'azure_local':
            return 'azure.png';
        case 'gcp':
            return 'favicon_gcp.png';
        case 'hyperv':
            return 'favicon_hyperv.png';
        case 'nc2':
            return 'favicon_nc2.svg';
        case 'openshift':
            return 'favicon_openshift.png';
        case 'openstack':
            return 'favicon_openstack.png';
        case 'rivermeadow_standalone':
            return 'vsphere.png';
        case 'hpe_vme':
            return 'favicon_hpe_vme.png';
        default:
            return 'vsphere.png';
    }
};

export const getMigrationApplianceURL = (item) => {
    const defaultUrl = item?.url || "";
    const cloudConfig = item?.appliance?.cloud_configuration;

    if (!cloudConfig) {
        return defaultUrl;
    }

    switch (item?.type) {
        case "rivermeadow_standalone":
            return cloudConfig?.vsphere?.url || defaultUrl;
        case "openshift":
            return cloudConfig?.openshift?.url || defaultUrl;
        case "nc2":
            return cloudConfig?.nc2?.url || defaultUrl;
        case "hpe_vme":
            return cloudConfig?.hpe_vme?.url || defaultUrl;
        case "hyperv":
            return cloudConfig?.hyperv?.url || defaultUrl;
        default:
            return defaultUrl;
    }
};

export const getApplianceReadiness = (item, state_field_info) => {
    let dual_heartbeat_status = {
        appliance_heartbeat_status: item.appliance ? item.appliance.heartbeat_status : null,
    };
    let vm_level_migration_enabled =
        item.appliance?.vm_level_migration_enabled ||
        (item.type === 'rivermeadow_standalone' &&
            item.appliance?.cloud_configuration &&
            item.appliance?.cloud_configuration.vsphere &&
            item.appliance?.cloud_configuration.vsphere.vm_level_migrations_enabled) ||
        ((item.type === 'openshift' || item.type === 'nc2' || item.type === 'hpe_vme' || item.type === 'hyperv') &&
            item.appliance?.cloud_configuration &&
            item.appliance.cloud_configuration.vsphere);
    if (vm_level_migration_enabled) {
        let source_worker_heartbeat_status = [];
        if (item.appliance) {
            let source_workers = item.appliance.source_workers ? item.appliance.source_workers : [];
            for (
                let i = 0;
                i < (item.appliance.source_clouds?.vsphere?.deployment_configuration?.source_workers_count || 1);
                i++
            ) {
                if (i < source_workers.length) {
                    source_worker_heartbeat_status.push(!!source_workers[i].heartbeat_status);
                } else {
                    source_worker_heartbeat_status.push(false);
                }
            }
        }
        dual_heartbeat_status.source_worker_heartbeat_status = source_worker_heartbeat_status;
    }
    return setHeartBeatStatusIcon(dual_heartbeat_status, item.appliance, state_field_info);
};

const setHeartBeatStatusIcon = (fieldVal, appliance, state_field_info) => {
    let title;
    const displayVal = [];
    if (fieldVal.appliance_heartbeat_status) {
        if (appliance?.infos && appliance.infos.length > 0) {
            title =
                state_field_info.appliance_success_title +
                '\n' +
                appliance.infos.reduce((infos, info) => infos + '\n' + info);
            displayVal.push(
                <Tooltip key="appliance-success-multi" title={title}>
                    <CheckCircleOutlined className="warning" />
                </Tooltip>,
            );
        } else {
            displayVal.push(
                <Tooltip key="appliance-success-multi" title={state_field_info.appliance_success_title}>
                    <CheckCircleOutlined className="success" />
                </Tooltip>,
            );
        }
    } else {
        displayVal.push(
            <Tooltip key="appliance-success-multi" title={state_field_info.appliance_fail_title}>
                <ExclamationCircleOutlined className="not-ready" />
            </Tooltip>,
        );
    }
    // Rename the field value to keep a tooltip the same as it was for state (before second heartbeat was introduced)
    fieldVal.appliance_heartbeat_status = fieldVal.appliance_heartbeat_status ? 'success' : 'no-heartbeat';

    if (fieldVal.hasOwnProperty('source_worker_heartbeat_status')) {
        fieldVal.source_worker_heartbeat_status.forEach((status, idx) => {
            if (status) {
                displayVal.push(
                    <Tooltip key={`worker-success-${idx}`} title={state_field_info.source_worker_success_title}>
                        <CheckCircleOutlined className="success" />
                    </Tooltip>,
                );
            } else {
                displayVal.push(
                    <Tooltip key={`worker-fail-${idx}`} title={state_field_info.source_worker_fail_title}>
                        <ExclamationCircleOutlined className="not-ready" />
                    </Tooltip>,
                );
            }
        });
    }
    return <div className="appliance-readiness-content">{displayVal}</div>;
};

export const getApplianceDeploymentState = (appliance) => {
    let processState = appliance ? appliance.process_state : 'pending';
    let instanceState = appliance?.instance_state;
    let heartbeatState = appliance ? appliance.heartbeat_status : false;
    let upgradeState = appliance?.upgrade_state;
    let errorInfo = appliance ? appliance.error : ''; //may or may not be empty, but capture it
    let upgradeErrorInfo = appliance
        ? appliance.upgrade_error_message || appliance.upgrade_error || 'Unknown'
        : 'Unknown';

    let stateTranslation = {
        success: {
            translation: 'Deployed',
        },
        /* These are handled above, I want to keep just in case.
        "running": {
            translation: "In Progress - Deploying"
        },
        "error": {
            translation: "Error"
        },
*/
        pending: {
            translation: 'Not Deployed',
        },
        'not-deployed': {
            translation: 'Not Deployed',
        },
        none: {
            translation: 'Not Deployed',
        },
    };

    let deploymentState;
    switch (processState) {
        case 'running':
            deploymentState = <InProgress customText={makeNiceName(instanceState)} />;
            break;
        case 'error':
            deploymentState = (
                <>
                    <Tooltip title={errorInfo}>
                        <CloseCircleOutlined className="error" />
                    </Tooltip>{' '}
                    Error
                </>
            );
            break;
        default:
            if (upgradeState === 'running') {
                deploymentState = <InProgress customText={makeNiceName('upgrading')} />;
            } else if (upgradeState === 'error') {
                deploymentState = (
                    <>
                        <Tooltip title={upgradeErrorInfo}>
                            <CloseCircleOutlined className="error" />
                        </Tooltip>{' '}
                        Upgrade Error
                    </>
                );
            } else {
                deploymentState = stateTranslation[processState].translation;
            }
    }
    // If the appliance is heartbeating, clear all other states, except if upgrading
    if (heartbeatState) {
        if (upgradeState === 'running') {
            deploymentState = <InProgress customText={makeNiceName('upgrading')} />;
        } else if (upgradeState === 'error') {
            deploymentState = (
                <>
                    <Tooltip title={upgradeErrorInfo}>
                        <CloseCircleOutlined className="error" />
                    </Tooltip>{' '}
                    Upgrade Error
                </>
            );
        } else {
            deploymentState = 'Deployed';
        }
    }
    return <span>{deploymentState}</span>;
};

export const formatTags = (tags) => {
    if (!tags || tags.length === 0) return '-';

    return tags.map((tag, index) => <RivermeadowTag key={index}>{tag}</RivermeadowTag>);
};

export const showHeartbeatStatus = (appliance) => {
    return appliance?.heartbeat_status && appliance?.heartbeat_at ? (
        <div className="appliance-details-value">
            <CheckCircleOutlined className="success" />
            <span>Ready to use</span>
        </div>
    ) : (
        <span className="warning">Not Ready - Please wait</span>
    );
};

export const showLastHeartbeat = (appliance) => {
    return appliance?.heartbeat_at ? (
        <span>{new Date(appliance?.heartbeat_at).toLocaleString(undefined, { hour12: false })}</span>
    ) : (
        <span className="warning">Waiting for heartbeat</span>
    );
};

export const returnAgentUrl = (appliance, cloudAccountType) => {
    let cloudTypes = ['azure_dr', 'vsphere_dr', 'vsphere_offline_replicas'];
    if (appliance.webminURL && !cloudTypes.includes(cloudAccountType)) {
        let parsedURL = new URL(appliance.webminURL);
        let url = `${parsedURL.protocol}//${parsedURL.hostname}:8888/agent`;
        return (
            <a href={url} target="_blank" rel="noopener noreferrer">
                {url}
            </a>
        );
    } else {
        return null;
    }
};

export const isVmLevelMigrationEnabled = (cloudAccountData) => {
    if (cloudAccountData?.appliance?.vm_level_migration_enabled) return true;

    if (
        cloudAccountData?.type == 'openshift' ||
        cloudAccountData?.type == 'nc2' ||
        cloudAccountData?.type == 'hpe_vme'
    ) {
        if (cloudAccountData?.appliance?.cloud_configuration?.vsphere) return true;
    }

    return false;
};

export const showSourceWorkers = (cloudAccountData) => {
    if (isVmLevelMigrationEnabled(cloudAccountData)) return true;
    if (cloudAccountData?.appliance?.cloud_configuration?.vsphere?.vm_level_migrations_enabled) return true;
    if (cloudAccountData?.type === 'vsphere_offline_replicas') return true;

    return false;
};

export const deploymentConfiguration = (appliance) => {
    let deploymentConfiguration = [];
    if (appliance?.cloud_configuration?.vsphere) {
        for (
            let i = 0;
            i < (appliance.source_clouds?.vsphere?.deployment_configuration?.source_workers_count || 1);
            i++
        ) {
            let source_worker_number = i ? '_' + i : '';
            let cloud_configuration = appliance.cloud_configuration.vsphere;
            let current_source_worker = appliance?.source_workers?.[i];
            let parsedSourceWorkerURL = new URL(appliance.webminURL);
            let source_worker_url;
            let parsedURL = new URL(appliance.webminURL);
            if (current_source_worker?.ip_address) {
                source_worker_url = parsedURL.protocol + '//' + current_source_worker?.ip_address + ':10000/';
            }
            deploymentConfiguration.push({
                datacenter: cloud_configuration.datacenter,
                source_worker_url,
                source_worker_heartbeat: {
                    heartbeat_status: current_source_worker?.heartbeat_status,
                    heartbeat_at: current_source_worker?.heartbeat_at,
                },
                source_worker_cluster: cloud_configuration['source_worker_cluster' + source_worker_number],
                source_worker_datastore: cloud_configuration['source_worker_datastore' + source_worker_number],
                source_worker_dns1: cloud_configuration['source_worker_dns1' + source_worker_number],
                source_worker_dns2: cloud_configuration['source_worker_dns2' + source_worker_number],
                source_worker_folder: cloud_configuration['source_worker_folder' + source_worker_number],
                source_worker_gateway: cloud_configuration['source_worker_gateway' + source_worker_number],
                source_worker_ip: cloud_configuration['source_worker_ip' + source_worker_number],
                source_worker_ip_type: cloud_configuration['source_worker_ip_type' + source_worker_number],
                source_worker_netmask: cloud_configuration['source_worker_netmask' + source_worker_number],
                source_worker_network: cloud_configuration['source_worker_network' + source_worker_number],
                source_worker_resource_pool: cloud_configuration['source_worker_resource_pool' + source_worker_number],
                source_worker_vm_name: cloud_configuration['source_worker_vm_name' + source_worker_number],
                source_worker_configuration_error: current_source_worker?.heartbeat_at
                    ? undefined
                    : cloud_configuration['source_worker_configuration_error' + source_worker_number],
            });
        }
    }

    return deploymentConfiguration;
};

export const sourceWorkerHeartbeatData = (appliance) => {
    let sourceWorker = {};

    if (appliance.source_workers && appliance.source_workers.length) {
        sourceWorker.heartbeat_status = appliance.source_workers[0].heartbeat_status;
        sourceWorker.heartbeat_at = appliance.source_workers[0].heartbeat_at;
        if (
            compareVersions(appliance.appliance_original_version_num, '0.113.5938') > 0 &&
            appliance.source_workers[0].ip_address
        ) {
            let parsedURL = new URL(appliance.webminURL);
            sourceWorker.webminURL = parsedURL.protocol + '//' + appliance.source_workers[0].ip_address + ':10000/';
        }
        return sourceWorker;
    }
};

export const dataCenterValue = (config) => {
    let dataCenterValue = config.source_datacenter || config.datacenter;
    return dataCenterValue || false;
};

export const canApplianceUpgrade = (appliance) => {
    let applianceLatestVersion = getSessionVar('applianceLatestVersion');
    let targetApplianceCurrentVersion = getValFromPath(appliance, 'appliance.appliance_version_num');
    let processState = getValFromPath(appliance, 'appliance.process_state');
    let upgradeState = getValFromPath(appliance, 'appliance.upgrade_state');
    let heartbeat = getValFromPath(appliance, 'appliance.heartbeat_status');
    let versionUnUpgradeable = !isApplianceUpgradeableToVersion(targetApplianceCurrentVersion, applianceLatestVersion);
    let versionTooOld = compareVersions(targetApplianceCurrentVersion, CONFIG.CUTOFF_VERSION_FOR_UPGRADE_AVAIL) < 0;

    if (versionTooOld || versionUnUpgradeable) {
        return false;
    }

    if (!heartbeat) {
        return false;
    }

    if (processState === 'running' || upgradeState === 'pending' || upgradeState === 'running') {
        return false;
    }

    let currentCompRes = compareVersions(targetApplianceCurrentVersion, applianceLatestVersion);
    return currentCompRes < 0;
};

// Validation function for IP addresses and FQDNs - matches Backbone logic
export const validateSourceIP = (value) => {
    const FQDN_regex = /(?=^.{4,253}$)(^((?!-)[a-zA-Z0-9-]{0,62}[a-zA-Z0-9]\.)+[a-zA-Z]{2,63}$)/;

    const trimmedValue = value?.trim();
    return isValidIP(trimmedValue) || FQDN_regex.test(trimmedValue);
};

// Network troubleshooting utilities configuration
export const networkTroubleshootingUtilities = [
    { id: 'ping', label: 'Ping' },
    { id: 'nslookup', label: 'Nslookup' },
    { id: 'nmap', label: 'Nmap' },
    { id: 'dig', label: 'Dig' },
    { id: 'traceroute', label: 'Traceroute' },
    { id: 'latency', label: 'Latency' },
];

// Network Troubleshooting utility function
export const canTroubleshoot = (item) => {
    // Replicated enabling function from cloud_list_config.js line 266
    const heartbeat = item?.appliance?.heartbeat_status;
    const targetApplianceOriginalVersion = item?.appliance?.appliance_original_version_num;
    const CUTOFF_VERSION_FOR_NETWORK_TROUBLESHOOTING = '0.97.3589';

    if (!targetApplianceOriginalVersion) return false;

    const versionTooOld =
        compareVersions(targetApplianceOriginalVersion, CUTOFF_VERSION_FOR_NETWORK_TROUBLESHOOTING) < 0;

    // Check if appliance is available (matches RM.isApplianceAvailable logic)

    if (heartbeat === true) {
        const isApplianceAvailable = true;
    } else {
        const processState = item?.appliance?.process_state;
        if (processState === 'running') {
            const isApplianceAvailable = false;
        } else {
            const isApplianceAvailable = true;
        }
    }

    // Determine final availability (matches RM.canTroubleshoot logic)
    let isApplianceAvailable;
    if (heartbeat === true) {
        isApplianceAvailable = true;
    } else {
        const processState = item?.appliance?.process_state;
        isApplianceAvailable = processState !== 'running';
    }

    return heartbeat && isApplianceAvailable && !versionTooOld;
};

export const canRedeploy = (item) => {
    const cloudType = item?.type;
    const processState = item?.appliance?.process_state;

    // Only allow redeploy for public cloud types
    const supportedTypes = [CLOUD_TYPE.AWS, CLOUD_TYPE.AZURE, CLOUD_TYPE.AZURE_LOCAL, CLOUD_TYPE.GCP];
    if (!supportedTypes.includes(cloudType)) {
        return false;
    }

    // Don't allow redeploy if appliance is currently deploying
    if (processState === ApplianceStates.RUNNING || processState === ApplianceStates.PENDING) {
        return false;
    }

    return true;
};
